<template>
  <div class="mypage rabiitrip-screen">
    <AppHeader />

    <div class="mypage-content rabiitrip-shell">
      <div class="container mypage-unified-container">
        <!-- ページ最上部タイトルヘッダー（評価ダッシュボードと統一） -->
        <header class="dashboard-header">
          <div class="header-titles">
            <p class="dashboard-kicker">
              {{ mainTab === 'shiori' ? '旅程の確認 & 編集' : '行き先の相談 & 比較' }}
            </p>
            <h1 class="dashboard-title page-title">
              {{ mainTab === 'shiori' ? '旅程管理' : '旅行先検討' }}
            </h1>
            <p class="dashboard-desc">
              {{ mainTab === 'shiori'
                ? '作成したしおりや旅行スケジュールを管理・確認できます。'
                : '参加メンバーと一緒に候補地を出し合い、比較・検討して旅行先を決めましょう。'
              }}
            </p>
          </div>
        </header>

        <!-- 旅行全体フロー図（旅程管理/旅先検討に応じたステップ表示） -->
        <TripWorkflowDiagram :current-step="mainTab === 'shiori' ? 2 : 1" />

        <!-- マイページ共通タブ -->
        <div class="mypage-main-tabs">
          <button
            class="main-tab-btn"
            :class="{ active: mainTab === 'shiori' }"
            @click="goToMainTab('shiori')"
          >
            旅行一覧
          </button>
          <button
            class="main-tab-btn"
            :class="{ active: mainTab === 'board' }"
            @click="goToMainTab('board')"
          >
            旅行先検討
          </button>
        </div>

        <div v-if="mainTab === 'shiori'">
          <!-- 検索バー + ゴミ箱 -->
          <div class="search-and-tools-bar">
            <div class="search-bar">
              <input
                v-model="searchQuery"
                type="text"
                class="search-input"
                placeholder="しおりを検索..."
              />
              <button
                v-if="searchQuery"
                class="search-clear-btn"
                @click="searchQuery = ''"
                aria-label="クリア"
              >
                ×
              </button>
            </div>
            <router-link to="/trash" class="trash-link-btn" title="ゴミ箱">
              <IconSvg name="trash-solid-full" size="sm" class="trash-icon" />
              <span>ゴミ箱</span>
            </router-link>
          </div>

          <!-- タブ切り替え -->
          <div
            v-if="
              !shioriStore.isLoading &&
              (shioriStore.shiori.length > 0 ||
                shioriStore.sharedShiori.length > 0)
            "
            class="destination-tabs"
          >
            <button
              class="tab-button"
              :class="{ active: activeTab === 'all' }"
              @click="activeTab = 'all'"
            >
              すべて ({{ filteredBySearch.length }})
            </button>
            <button
              class="tab-button"
              :class="{ active: activeTab === 'shared' }"
              @click="activeTab = 'shared'"
            >
              <IconSvg name="user-solid-full" size="sm" /> 共有されたしおり ({{
                shioriStore.sharedShiori.length
              }})
            </button>
            <button
              class="tab-button"
              :class="{ active: activeTab === 'domestic' }"
              @click="activeTab = 'domestic'"
            >
              JP 国内 ({{ domesticShiori.length }})
            </button>
            <button
              class="tab-button"
              :class="{ active: activeTab === 'international' }"
              @click="activeTab = 'international'"
            >
              <IconSvg name="earth-americas-solid-full" size="sm" /> 海外 ({{
                internationalShiori.length
              }})
            </button>
          </div>

          <!-- しおり一覧 -->
          <div v-if="shioriStore.isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>読み込み中...</p>
          </div>

          <div
            v-else-if="
              shioriStore.shiori.length === 0 &&
              shioriStore.sharedShiori.length === 0
            "
            class="empty-state"
          >
            <div class="empty-icon">
              <IconSvg name="plane-solid-full" size="xl" />
            </div>
            <h3 class="empty-title">しおりがまだありません</h3>
            <p class="empty-description">
              右下のボタンから新しい旅のしおりを作成しましょう！
            </p>
          </div>

          <div v-else-if="filteredShiori.length === 0" class="empty-state">
            <div class="empty-icon">
              <IconSvg name="envelope-solid-full" size="xl" />
            </div>
            <h3 class="empty-title">該当するしおりがありません</h3>
            <p class="empty-description">他のタブを確認してみてください</p>
          </div>

          <!-- 共有されたしおりタブ -->
          <div v-else-if="activeTab === 'shared'" class="shiori-grid">
            <TripCard
              v-for="item in filteredShiori"
              :key="item.SHIORI_NOV"
              :title="item.SUBJECT_NMV"
              :members="shioriMembers[item.SHIORI_NOV] || []"
              :start-date="formatDate(item.TRIP_FROM)"
              :end-date="formatDate(item.TRIP_TO)"
              :shiori-id="item.SHIORI_NOV"
              :has-image="item.HAS_IMAGE === true"
              :image-src="item.IMAGE_DATA_URI || item.IMAGE_URL || ''"
              :is-guest="item.__guest === true"
              :total-cost="getTotalCost(item)"
              :permission-level="getShioriPermission(item)"
              @manage="handleManage"
              @share="handleShare"
              @copy="handleCopy"
              @delete="handleDelete"
              @click="goToShiori(item.SHIORI_NOV)"
            />
          </div>

          <!-- 地図表示エリア（国内・海外タブのみ） -->
          <div
            v-else-if="activeTab === 'domestic'"
            class="map-and-list-container"
          >
            <div class="map-section">
              <div class="destination-count-card">
                <div class="count-icon">JP</div>
                <div class="count-info">
                  <div class="count-label">訪問済み</div>
                  <div class="count-number">
                    <span class="count-current">{{
                      domesticDestinationCount
                    }}</span>
                    <span class="count-separator">/</span>
                    <span class="count-total">{{ TOTAL_PREFECTURES }}</span>
                  </div>
                  <div class="count-unit">都道府県</div>
                </div>
              </div>
              <JapanMapSVG
                :shiori-list="visitedDomesticShiori"
                @shiori-click="goToShiori"
              />
            </div>
            <div class="list-section">
              <div class="shiori-grid">
                <TripCard
                  v-for="item in filteredShiori"
                  :key="item.SHIORI_NOV"
                  :title="item.SUBJECT_NMV"
                  :members="shioriMembers[item.SHIORI_NOV] || []"
                  :start-date="formatDate(item.TRIP_FROM)"
                  :end-date="formatDate(item.TRIP_TO)"
                  :shiori-id="item.SHIORI_NOV"
                  :has-image="item.HAS_IMAGE === true"
                  :image-src="item.IMAGE_DATA_URI || item.IMAGE_URL || ''"
                  :is-guest="item.__guest === true"
                  :total-cost="getTotalCost(item)"
                  :permission-level="getShioriPermission(item)"
                  @manage="handleManage"
                  @share="handleShare"
                  @copy="handleCopy"
                  @delete="handleDelete"
                  @click="goToShiori(item.SHIORI_NOV)"
                />
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'international'"
            class="map-and-list-container"
          >
            <div class="map-section">
              <div class="destination-count-card">
                <div class="count-icon">
                  <IconSvg name="earth-americas-solid-full" size="lg" />
                </div>
                <div class="count-info">
                  <div class="count-label">訪問済み</div>
                  <div class="count-number">
                    <span class="count-current">{{
                      internationalDestinationCount
                    }}</span>
                    <span class="count-separator">/</span>
                    <span class="count-total">{{ TOTAL_COUNTRIES }}</span>
                  </div>
                  <div class="count-unit">カ国</div>
                </div>
              </div>
              <WorldMapSVG
                :shiori-list="visitedInternationalShiori"
                @shiori-click="goToShiori"
              />
            </div>
            <div class="list-section">
              <div class="shiori-grid">
                <TripCard
                  v-for="item in filteredShiori"
                  :key="item.SHIORI_NOV"
                  :title="item.SUBJECT_NMV"
                  :members="shioriMembers[item.SHIORI_NOV] || []"
                  :start-date="formatDate(item.TRIP_FROM)"
                  :end-date="formatDate(item.TRIP_TO)"
                  :shiori-id="item.SHIORI_NOV"
                  :has-image="item.HAS_IMAGE === true"
                  :image-src="item.IMAGE_DATA_URI || item.IMAGE_URL || ''"
                  :is-guest="item.__guest === true"
                  :total-cost="getTotalCost(item)"
                  :permission-level="getShioriPermission(item)"
                  @manage="handleManage"
                  @share="handleShare"
                  @copy="handleCopy"
                  @delete="handleDelete"
                  @click="goToShiori(item.SHIORI_NOV)"
                />
              </div>
            </div>
          </div>

          <div v-else class="shiori-grid">
            <TripCard
              v-for="item in pagedShiori"
              :key="item.SHIORI_NOV"
              :title="item.SUBJECT_NMV"
              :members="shioriMembers[item.SHIORI_NOV] || []"
              :start-date="formatDate(item.TRIP_FROM)"
              :end-date="formatDate(item.TRIP_TO)"
              :shiori-id="item.SHIORI_NOV"
              :has-image="item.HAS_IMAGE === true"
              :image-src="item.IMAGE_DATA_URI || item.IMAGE_URL || ''"
              :is-guest="item.__guest === true"
              :total-cost="getTotalCost(item)"
              :permission-level="getShioriPermission(item)"
              @manage="handleManage"
              @share="handleShare"
              @copy="handleCopy"
              @delete="handleDelete"
              @click="goToShiori(item.SHIORI_NOV)"
            />
          </div>

          <!-- ページネーション -->
          <div v-if="activeTab === 'all' && totalPages > 1" class="pagination">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="currentPage--"
            >
              ‹ 前へ
            </button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              次へ ›
            </button>
          </div>
        </div>

        <div v-else-if="mainTab === 'board'">
          <!-- AI渡航先相談（新提案）カード -->
          <router-link
            to="/ai-destination-suggest"
            class="ai-suggest-card"
            id="btn-goto-ai-suggest"
            data-track="mypage-goto-ai-suggest"
          >
            <div class="ai-suggest-icon">
              <IconSvg name="earth-americas-solid-full" size="lg" />
            </div>
            <div class="ai-suggest-copy">
              <span class="ai-suggest-label">AI TRIP SUGGEST</span>
              <strong>AIに旅行先を相談する</strong>
              <span
                >条件を指定してAIにおすすめの旅行先を提案してもらいましょう</span
              >
            </div>
            <span class="ai-suggest-arrow">→</span>
          </router-link>

          <!-- 検討ボード一覧 -->
          <div v-if="boardStore.isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>読み込み中...</p>
          </div>

          <div v-else-if="boardStore.boards.length === 0" class="empty-state">
            <div class="empty-icon">
              <IconSvg name="map-solid-full" size="xl" />
            </div>
            <h3 class="empty-title">検討ボードがまだありません</h3>
            <p class="empty-description">
              右下のボタンから新しい検討ボードを作成するか、AIに提案してもらいましょう！
            </p>
          </div>

          <div v-else class="shiori-grid">
            <div
              v-for="board in boardStore.boards"
              :key="board.BOARD_ID"
              class="board-card"
              @click="goToBoard(board.BOARD_ID)"
            >
              <div class="board-card-content">
                <h3 class="board-title">{{ board.BOARD_TITLE }}</h3>
                <div class="board-meta">
                  <span class="board-members">
                    <IconSvg name="user-solid-full" size="xs" />
                    {{
                      board.members
                        ? board.members.length
                        : board.MEMBER_COUNT || 1
                    }}人
                  </span>
                  <span class="board-updated">
                    最終更新: {{ formatDate(board.UPDATE_YMD) }}
                  </span>
                </div>
              </div>
              <div class="board-arrow">→</div>
            </div>
          </div>

          <section class="destination-template-section" aria-labelledby="destination-template-title">
            <div class="destination-template-heading">
              <div>
                <span class="destination-template-kicker">BOARD TEMPLATES</span>
                <h2 id="destination-template-title">テンプレートから検討を始める</h2>
              </div>
              <span class="destination-template-count">{{ destinationBoardTemplates.length }}件</span>
            </div>
            <p class="destination-template-lead">
              用意された候補地を自分のボードへコピーします。AI相談は実行されません。
            </p>
            <div
              v-if="templateCopyError"
              class="destination-template-error"
              role="alert"
            >
              {{ templateCopyError }} 同じ操作として再試行できます。
            </div>
            <div
              v-if="templateCatalogError"
              class="destination-template-error"
              role="alert"
            >
              {{ templateCatalogError }}
              <button
                type="button"
                class="destination-template-reload"
                :disabled="isTemplateCatalogLoading"
                @click="loadDestinationBoardTemplates"
              >
                テンプレートを再読み込み
              </button>
            </div>
            <p
              v-else-if="isTemplateCatalogLoading"
              class="destination-template-status"
              role="status"
              aria-live="polite"
            >
              テンプレートを確認中…
            </p>
            <div class="destination-template-grid">
              <button
                v-for="template in destinationBoardTemplates"
                :key="template.templateKey"
                type="button"
                class="destination-template-card"
                :data-testid="`destination-template-${template.templateKey}`"
                :aria-label="templateButtonLabel(template)"
                :aria-busy="copyingTemplateKey === template.templateKey ? 'true' : 'false'"
                :aria-describedby="!isTemplateCatalogLoading && !templateCatalogError && !availableTemplateKeys.has(template.templateKey) ? `template-unavailable-${template.templateKey}` : undefined"
                :disabled="copyingTemplateKey !== null || !availableTemplateKeys.has(template.templateKey)"
                @click="copyDestinationBoardTemplate(template.templateKey)"
              >
                <span class="destination-template-icon" aria-hidden="true">
                  <IconSvg :name="template.icon" size="md" />
                </span>
                <span class="destination-template-copy">
                  <small>{{ template.badge }}</small>
                  <strong>{{ template.title }}</strong>
                  <span>{{ template.conditions }}</span>
                  <span>{{ template.description }}</span>
                  <span
                    v-if="!isTemplateCatalogLoading && !templateCatalogError && !availableTemplateKeys.has(template.templateKey)"
                    :id="`template-unavailable-${template.templateKey}`"
                    class="destination-template-unavailable"
                  >
                    候補地の準備が完了していないため、現在はコピーできません。
                  </span>
                </span>
                <span
                  class="destination-template-arrow"
                  role="status"
                  aria-live="polite"
                >
                  {{
                    copyingTemplateKey === template.templateKey
                      ? "コピー中…"
                      : availableTemplateKeys.has(template.templateKey)
                        ? failedTemplateKey === template.templateKey
                          ? "再試行"
                          : "コピー"
                        : "準備中"
                  }}
                </span>
              </button>
            </div>
          </section>
        </div>

        <section v-if="mainTab === 'shiori'" class="shiori-template-section">
          <div class="template-section-heading">
            <div>
              <span class="template-kicker">しおりテンプレート</span>
              <h2>人気の旅程から作成</h2>
            </div>
            <span class="template-count">{{ shioriTemplates.length }}件</span>
          </div>
          <div class="shiori-template-grid">
            <button
              v-for="template in shioriTemplates"
              :key="template.id"
              :data-testid="`mypage-template-${template.id}`"
              type="button"
              class="shiori-template-card"
              :aria-label="`${template.title}（${template.days}日間）を選ぶ`"
              :disabled="creatingTemplateId === template.id"
              @click="openShioriTemplateModal(template)"
            >
              <span class="template-card-image-wrap">
                <img
                  class="template-card-image"
                  :src="template.imageUrl"
                  :alt="template.imageAlt"
                  width="1600"
                  height="900"
                  loading="lazy"
                  decoding="async"
                  @error="hideBrokenTemplateImage"
                />
              </span>
              <span class="template-card-main">
                <strong>{{ template.title }}</strong>
                <small>{{ template.description }}</small>
              </span>
              <span class="template-card-meta">
                {{
                  creatingTemplateId === template.id
                    ? "作成中..."
                    : `${template.days}日間`
                }}
              </span>
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- 新規作成プラスボタン -->
    <FloatingAddButton
      v-if="!showModal && !showCreateBoardModal"
      :variant="mainTab === 'board' ? 'board' : 'shiori'"
      :aria-label="mainTab === 'board' ? '新規検討ボードを作成' : 'Create shiori'"
      @click="handleFloatingAddClick"
    />

    <!-- 新規shiori作成モーダル -->
    <CreateShioriModal
      v-model="showModal"
      :prefill-data="createModalPrefillData"
      @create="onShioriCreated"
      @template-create-failed="onTemplateCreateFailed"
    />

    <!-- しおり管理モーダル -->
    <ManageShioriModal
      v-model="showManageModal"
      :shiori-data="selectedShiori"
      :can-write="selectedCanWrite"
      :can-manage="selectedCanManage"
      :can-delete="selectedIsOwner"
      :can-delete-members="selectedIsOwner"
      @updated="onShioriUpdated"
      @deleted="onShioriDeleted"
    />

    <!-- 共有モーダル -->
    <ShareShioriModal
      v-model="showShareModal"
      :shiori-id="selectedShioriId"
      :can-manage="selectedCanManage"
    />

    <!-- コピー/削除確認モーダル -->
    <transition name="modal-fade">
      <div
        v-if="confirmDialog.isOpen"
        class="modal-overlay native-confirm-overlay"
        @click.self="closeConfirmDialog"
      >
        <div
          class="modal-container native-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="native-confirm-title"
          aria-describedby="native-confirm-description"
        >
          <div class="modal-header native-confirm-header">
            <h2 id="native-confirm-title" class="modal-title native-confirm-title">
              {{ confirmDialog.title }}
            </h2>
            <button
              type="button"
              class="modal-close-btn native-confirm-close"
              aria-label="閉じる"
              :disabled="shioriStore.isLoading"
              @click="closeConfirmDialog"
            >
              ×
            </button>
          </div>

          <p id="native-confirm-description" class="native-confirm-message">
            {{ confirmDialog.message }}
          </p>

          <div class="native-confirm-actions">
            <button
              type="button"
              class="native-confirm-cancel"
              :disabled="shioriStore.isLoading"
              @click="closeConfirmDialog"
            >
              キャンセル
            </button>
            <button
              type="button"
              class="native-confirm-accept"
              :class="{ danger: confirmDialog.action === 'delete' }"
              :disabled="shioriStore.isLoading"
              @click="confirmShioriAction"
            >
              {{ confirmDialog.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 新規検討ボード作成モーダル -->
    <transition name="modal-fade">
      <div
        v-if="showCreateBoardModal"
        class="modal-overlay board-create-overlay"
        @click.self="closeCreateBoardModal"
      >
        <div
          class="modal-container board-create-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="board-create-title"
        >
          <div class="modal-header board-create-header">
            <h2 id="board-create-title" class="modal-title board-create-title">
              <IconSvg name="map-solid-full" size="md" /> 新規検討ボード作成
            </h2>
            <button
              type="button"
              class="modal-close-btn board-create-close"
              aria-label="閉じる"
              @click="closeCreateBoardModal"
              :disabled="boardStore.isLoading"
            >
              ×
            </button>
          </div>

          <div v-if="boardError" class="error-message board-create-error" role="alert">
            {{ boardError }}
          </div>

          <form @submit.prevent="handleCreateBoard" class="modal-form board-create-form">
            <div class="form-group">
              <label class="form-label">
                ボード名 <span class="required">*</span>
              </label>
              <input
                v-model="newBoardTitle"
                type="text"
                class="form-input"
                :class="{ 'input-error': boardError }"
                :aria-invalid="boardError ? 'true' : 'false'"
                :disabled="boardStore.isLoading"
                placeholder="例: 夏休みの旅行計画"
                maxlength="255"
                required
              />
            </div>

            <div class="modal-footer board-create-footer">
              <button
                type="button"
                class="btn btn-secondary board-create-secondary"
                @click="closeCreateBoardModal"
                :disabled="boardStore.isLoading"
              >
                キャンセル
              </button>
              <button
                type="submit"
                class="btn btn-primary board-create-primary"
                id="btn-create-board-submit"
                data-track="mypage-create-board-submit"
                :disabled="boardStore.isLoading"
              >
                {{ boardStore.isLoading ? "作成中..." : "作成する" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { onMounted, onUnmounted, watch, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import AppHeader from "../components/AppHeader.vue";
import TripCard from "../components/TripCard.vue";
import FloatingAddButton from "../components/FloatingAddButton.vue";
import CreateShioriModal from "../components/CreateShioriModal.vue";
import ManageShioriModal from "../components/ManageShioriModal.vue";
import ShareShioriModal from "../components/ShareShioriModal.vue";
import JapanMapSVG from "../components/JapanMapSVG.vue";
import WorldMapSVG from "../components/WorldMapSVG.vue";
import IconSvg from "../components/IconSvg.vue";
import TripWorkflowDiagram from "@/components/TripWorkflowDiagram.vue";
import { useShioriStore } from "@/store/shiori-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useDestinationBoardStore } from "@/store/destination-board-store.js";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useNotificationStore } from "@/store/notification-store.js";
import { filterVisitedShiori, isTripVisited } from "@/utils/trip-status.js";
import {
  formatTime,
  getTripLiveState,
  isTripActiveToday,
} from "@/utils/trip-live.js";
import { fetchGuestSchedules, isGuestMode } from "@/utils/guest-session.js";
import { getISOCode } from "@/data/countryMapping.js";
import { shioriTemplates } from "@/data/shioriTemplates.js";
import { destinationBoardTemplates } from "@/data/destinationBoardTemplates.js";
import {
  createTemplateSchedulePayloads,
  createTemplateSchedulesWithRollback,
} from "@/utils/shiori-template.js";
import { API_URL } from "@/config/api.js";

export default {
  name: "MyPage",
  components: {
    AppHeader,
    TripCard,
    FloatingAddButton,
    CreateShioriModal,
    ManageShioriModal,
    ShareShioriModal,
    JapanMapSVG,
    WorldMapSVG,
    IconSvg,
    TripWorkflowDiagram,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const shioriStore = useShioriStore();
    const shioriMemberStore = useShioriMemberStore();
    const boardStore = useDestinationBoardStore();
    const notificationStore = useNotificationStore();
    const mainTab = computed(() =>
      route.name === "DestinationBoards" ? "board" : "shiori",
    );
    const scheduleStore = useScheduleStore();
    const showModal = ref(false);
    const showManageModal = ref(false);
    const showShareModal = ref(false);
    const showCreateBoardModal = ref(false);
    const newBoardTitle = ref("");
    const boardError = ref("");
    const copyingTemplateKey = ref(null);
    const templateCopyError = ref("");
    const templateCatalogError = ref("");
    const isTemplateCatalogLoading = ref(true);
    const failedTemplateKey = ref(null);
    const templateCopyAttemptKeys = new Map();

    const selectedShiori = ref(null);
    const selectedShioriId = ref(null);
    const shioriMembers = ref({}); // しおりIDをキーにメンバー配列を格納
    const activeTab = ref("all"); // タブの状態: 'all', 'shared', 'domestic', 'international'
    const searchQuery = ref(""); // 検索クエリ
    const currentPage = ref(1); // 現在のページ
    const liveNow = ref(new Date());
    const liveSchedulesByShioriId = ref({});
    const creatingTemplateId = ref(null);
    const pendingShioriTemplate = ref(null);
    const confirmDialog = ref({
      isOpen: false,
      action: null,
      shioriId: null,
      title: "",
      message: "",
      confirmText: "",
    });
    const loadingLiveScheduleIds = new Set();
    let liveTimerId = null;
    const PAGE_SIZE = 10; // 1ページあたりの件数
    const loadingMemberShioriIds = new Set();

    // 全体の都道府県数と国数
    const TOTAL_PREFECTURES = 47; // 日本の都道府県数
    const TOTAL_COUNTRIES = 195; // 国連加盟国数

    const parseTripDateTime = (value) => {
      if (!value) return null;
      if (value instanceof Date) {
        const time = value.getTime();
        return Number.isNaN(time) ? null : time;
      }

      const dateText = String(value).trim();
      const dateParts = dateText.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const date = dateParts
        ? new Date(
            Number(dateParts[1]),
            Number(dateParts[2]) - 1,
            Number(dateParts[3]),
          )
        : new Date(dateText);
      date.setHours(0, 0, 0, 0);

      const time = date.getTime();
      return Number.isNaN(time) ? null : time;
    };

    const getTodayTime = (referenceDate = new Date()) => {
      const today = new Date(referenceDate);
      today.setHours(0, 0, 0, 0);
      return today.getTime();
    };

    const compareShioriForDisplay = (a, b, referenceDate = new Date()) => {
      const aVisited = isTripVisited(a);
      const bVisited = isTripVisited(b);
      if (aVisited !== bVisited) {
        return aVisited ? 1 : -1;
      }

      const todayTime = getTodayTime(referenceDate);
      const aStartTime = parseTripDateTime(a?.TRIP_FROM);
      const bStartTime = parseTripDateTime(b?.TRIP_FROM);
      const aDistance =
        aStartTime === null
          ? Number.POSITIVE_INFINITY
          : Math.abs(aStartTime - todayTime);
      const bDistance =
        bStartTime === null
          ? Number.POSITIVE_INFINITY
          : Math.abs(bStartTime - todayTime);
      if (aDistance !== bDistance) {
        return aDistance - bDistance;
      }
      if (aStartTime !== bStartTime) {
        if (aStartTime === null) return 1;
        if (bStartTime === null) return -1;
        return aStartTime - bStartTime;
      }

      return String(a?.SHIORI_NOV || "").localeCompare(
        String(b?.SHIORI_NOV || ""),
      );
    };

    const sortShioriForDisplay = (shioriList) =>
      shioriList
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const result = compareShioriForDisplay(
            a.item,
            b.item,
            liveNow.value,
          );
          return result || a.index - b.index;
        })
        .map(({ item }) => item);

    // 一覧APIに含まれるJPY集計値をそのまま使う
    const getTotalCost = (shiori) => {
      const totalCost = Number(shiori?.TOTAL_COST_JPY ?? 0);
      return Number.isFinite(totalCost) ? totalCost : 0;
    };

    const normalizePermissionLevel = (permissionLevel) => {
      const permission = String(permissionLevel || "").trim().toUpperCase();
      if (permission === "OWNER" || permission === "ADMIN") return permission;
      if (permission === "WRITE" || permission === "EDIT") return "WRITE";
      return "READ";
    };

    const getShioriPermission = (shiori) => {
      if (!shiori?.SHIORI_NOV) return "READ";
      const owned = shioriStore.shiori.some(
        (item) => item.SHIORI_NOV === shiori.SHIORI_NOV,
      );
      if (owned) return "OWNER";
      return normalizePermissionLevel(
        shiori.permission_level || shiori.PERMISSION_LEVEL,
      );
    };

    const findShiori = (shioriId) =>
      [...shioriStore.shiori, ...shioriStore.sharedShiori].find(
        (item) => item.SHIORI_NOV === shioriId,
      );
    const canManageShiori = (shioriId) =>
      ["OWNER", "ADMIN"].includes(
        getShioriPermission(findShiori(shioriId)),
      );
    const isOwnerShiori = (shioriId) =>
      getShioriPermission(findShiori(shioriId)) === "OWNER";
    const selectedPermission = computed(() =>
      getShioriPermission(selectedShiori.value),
    );
    const selectedCanWrite = computed(() =>
      ["OWNER", "ADMIN", "WRITE"].includes(selectedPermission.value),
    );
    const selectedCanManage = computed(() =>
      ["OWNER", "ADMIN"].includes(selectedPermission.value),
    );
    const selectedIsOwner = computed(
      () => selectedPermission.value === "OWNER",
    );

    const loadMembersForShioriList = async (
      shioriList,
      { force = false } = {},
    ) => {
      const targets = shioriList.filter((shiori) => {
        const shioriId = shiori.SHIORI_NOV;
        if (loadingMemberShioriIds.has(shioriId)) {
          return false;
        }

        return force || shioriMembers.value[shioriId] === undefined;
      });

      await Promise.all(
        targets.map(async (shiori) => {
          const shioriId = shiori.SHIORI_NOV;
          loadingMemberShioriIds.add(shioriId);

          try {
            const members = await shioriMemberStore.fetchMembers(shioriId);
            shioriMembers.value[shioriId] = members.map(
              (member) => member.MEMBER_NAME,
            );
          } catch (error) {
            console.error(`メンバー取得エラー (${shioriId}):`, error);
            shioriMembers.value[shioriId] = [];
          } finally {
            loadingMemberShioriIds.delete(shioriId);
          }
        }),
      );
    };

    const loadDestinationBoardTemplates = async () => {
      isTemplateCatalogLoading.value = true;
      templateCatalogError.value = "";
      try {
        await boardStore.fetchBoardTemplates();
      } catch {
        templateCatalogError.value =
          "テンプレートの利用状況を確認できません。時間をおいて再読み込みしてください。";
      } finally {
        isTemplateCatalogLoading.value = false;
      }
    };

    onMounted(async () => {
      liveTimerId = window.setInterval(() => {
        liveNow.value = new Date();
      }, 60000);

      await Promise.all([
        shioriStore.fetchShiori(),
        shioriStore.fetchSharedWithMe(),
        boardStore.fetchBoards(),
        loadDestinationBoardTemplates(),
      ]);
    });

    onUnmounted(() => {
      if (liveTimerId) {
        window.clearInterval(liveTimerId);
      }
    });

    // 検索でフィルタリングされたしおり一覧 (タイトル・行先で絞り込み)
    const allShiori = computed(() => {
      const byId = new Map();
      [...shioriStore.shiori, ...shioriStore.sharedShiori].forEach((item) => {
        if (item?.SHIORI_NOV && !byId.has(item.SHIORI_NOV)) {
          byId.set(item.SHIORI_NOV, item);
        }
      });
      return sortShioriForDisplay(Array.from(byId.values()));
    });

    const sharedShioriForDisplay = computed(() =>
      sortShioriForDisplay(shioriStore.sharedShiori),
    );

    const loadLiveSchedulesForActiveTrips = async (shioriList) => {
      const targets = shioriList.filter((shiori) => {
        const shioriId = shiori.SHIORI_NOV;
        return (
          shioriId &&
          isTripActiveToday(shiori, liveNow.value) &&
          !liveSchedulesByShioriId.value[shioriId] &&
          !loadingLiveScheduleIds.has(shioriId)
        );
      });

      await Promise.all(
        targets.map(async (shiori) => {
          const shioriId = shiori.SHIORI_NOV;
          loadingLiveScheduleIds.add(shioriId);
          try {
            const schedules = isGuestMode()
              ? fetchGuestSchedules(shioriId)
              : (await axios.get(`${API_URL}/schedule/?shiori_id=${shioriId}`))
                  .data;
            liveSchedulesByShioriId.value = {
              ...liveSchedulesByShioriId.value,
              [shioriId]: schedules || [],
            };
          } catch (error) {
            console.error(`現在予定の取得エラー (${shioriId}):`, error);
            liveSchedulesByShioriId.value = {
              ...liveSchedulesByShioriId.value,
              [shioriId]: [],
            };
          } finally {
            loadingLiveScheduleIds.delete(shioriId);
          }
        }),
      );
    };

    const currentLiveCards = computed(() => {
      return allShiori.value
        .map((shiori) => {
          const schedules = liveSchedulesByShioriId.value[shiori.SHIORI_NOV];
          if (!schedules) return null;
          const state = getTripLiveState(schedules, shiori, liveNow.value);
          if (!state.currentItem) return null;
          return {
            shiori,
            item: state.currentItem,
            timeText: `${formatTime(state.currentItem.start)}-${formatTime(
              state.currentItem.effectiveEnd,
            )}`,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.item.start - b.item.start);
    });

    watch(
      allShiori,
      (shioriList) => {
        if (shioriList.length) {
          void loadLiveSchedulesForActiveTrips(shioriList);
        }
      },
      { immediate: true },
    );

    const filteredBySearch = computed(() => {
      const q = searchQuery.value.toLowerCase().trim();
      if (!q) return allShiori.value;
      return allShiori.value.filter((s) => {
        if (s.SUBJECT_NMV?.toLowerCase().includes(q)) return true;
        // 行先（都道府県名・国名）でも検索
        try {
          const dests = JSON.parse(s.DESTINATIONS || "[]");
          return (
            Array.isArray(dests) &&
            dests.some((d) => d.toLowerCase().includes(q))
          );
        } catch {
          return false;
        }
      });
    });

    const filteredSharedShiori = computed(() => {
      const q = searchQuery.value.toLowerCase().trim();
      if (!q) return sharedShioriForDisplay.value;
      return sharedShioriForDisplay.value.filter((s) => {
        if (s.SUBJECT_NMV?.toLowerCase().includes(q)) return true;
        try {
          const dests = JSON.parse(s.DESTINATIONS || "[]");
          return (
            Array.isArray(dests) &&
            dests.some((d) => d.toLowerCase().includes(q))
          );
        } catch {
          return false;
        }
      });
    });

    // 国内のしおりを抽出
    const domesticShiori = computed(() => {
      return filteredBySearch.value.filter((shiori) => {
        return (
          !shiori.DESTINATION_TYPE || shiori.DESTINATION_TYPE === "domestic"
        );
      });
    });

    // 海外のしおりを抽出
    const internationalShiori = computed(() => {
      return filteredBySearch.value.filter((shiori) => {
        return shiori.DESTINATION_TYPE === "international";
      });
    });

    // 地図・訪問済みカウント用（旅行終了後のみ）
    const visitedDomesticShiori = computed(() =>
      filterVisitedShiori(domesticShiori.value),
    );
    const visitedInternationalShiori = computed(() =>
      filterVisitedShiori(internationalShiori.value),
    );

    // 国内の渡航先（都道府県）数をカウント
    const domesticDestinationCount = computed(() => {
      const uniquePrefectures = new Set();
      visitedDomesticShiori.value.forEach((shiori) => {
        if (shiori.DESTINATIONS) {
          try {
            const destinations = JSON.parse(shiori.DESTINATIONS);
            destinations.forEach((dest) => uniquePrefectures.add(dest));
          } catch (error) {
            console.error("DESTINATIONSパースエラー:", error);
          }
        }
      });
      return uniquePrefectures.size;
    });

    // 海外の渡航先（国）数をカウント
    const internationalDestinationCount = computed(() => {
      const uniqueCountries = new Set();
      visitedInternationalShiori.value.forEach((shiori) => {
        if (shiori.DESTINATIONS) {
          try {
            const destinations = JSON.parse(shiori.DESTINATIONS);
            destinations.forEach((dest) =>
              uniqueCountries.add(getISOCode(dest) || dest),
            );
          } catch (error) {
            console.error("DESTINATIONSパースエラー:", error);
          }
        }
      });
      return uniqueCountries.size;
    });

    // タブに応じてフィルタリングされたしおり
    const filteredShiori = computed(() => {
      if (activeTab.value === "shared") {
        return filteredSharedShiori.value;
      } else if (activeTab.value === "domestic") {
        return domesticShiori.value;
      } else if (activeTab.value === "international") {
        return internationalShiori.value;
      }
      return filteredBySearch.value; // 'all'の場合
    });

    // ページネーション (すべてタブのみ)
    const totalPages = computed(() =>
      Math.max(1, Math.ceil(filteredShiori.value.length / PAGE_SIZE)),
    );

    const pagedShiori = computed(() => {
      const start = (currentPage.value - 1) * PAGE_SIZE;
      return filteredShiori.value.slice(start, start + PAGE_SIZE);
    });

    watch(
      pagedShiori,
      (shioriList) => {
        if (!shioriList.length) {
          return;
        }

        void loadMembersForShioriList(shioriList);
      },
      { immediate: true },
    );

    // タブまたは検索変更時はページを1に戻す
    watch([activeTab, searchQuery], () => {
      currentPage.value = 1;
    });

    // 日付フォーマット関数
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      const weekday = weekdays[date.getDay()];
      return `${year}年${String(month).padStart(2, "0")}月${String(
        day,
      ).padStart(2, "0")}日(${weekday})`;
    };

    const toLocalDateKey = (date) =>
      [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");

    const addDays = (dateKey, days) => {
      const [year, month, day] = dateKey.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + days);
      return toLocalDateKey(date);
    };

    const createModalPrefillData = computed(() => {
      const template = pendingShioriTemplate.value;
      if (!template) return null;
      const startDate = addDays(toLocalDateKey(new Date()), 30);
      const endDate = addDays(startDate, template.days - 1);

      return {
        title: template.title,
        startDate,
        endDate,
        days: template.days,
        budget: template.budget,
        destinationType: template.destinationType,
        destinations: template.destinations,
        isTemplate: true,
        requiresTemplateImageUpload: true,
        templateId: template.id,
        templateImageUrl: template.imageUrl,
        templateImageAlt: template.imageAlt,
      };
    });

    const resolveCreatedShioriId = (result, context = {}) =>
      context.shioriId ||
      result.SHIORI_NOV ||
      result.data?.SHIORI_NOV ||
      result.shiori_id;

    const createTemplateSchedules = async (
      template,
      shioriId,
      startDate,
      manualImageUploaded,
      templateImageFile,
    ) => {
      if (!template || !shioriId || !startDate) return;
      await createTemplateSchedulesWithRollback({
        payloads: [],
        createSchedule: () => {},
        finalize: async () => {
          const members = await shioriMemberStore.fetchMembers(shioriId, {
            force: true,
          });
          const payloads = createTemplateSchedulePayloads({
            template,
            shioriId,
            startDate,
            members: members || [],
          });
          for (const payload of payloads) {
            await scheduleStore.createSchedule(payload);
          }
        },
        rollback: () => shioriStore.deleteShiori(shioriId),
      });

      if (!manualImageUploaded && templateImageFile) {
        try {
          await shioriStore.uploadShioriImage(shioriId, templateImageFile);
        } catch (error) {
          console.error("テンプレート画像アップロードエラー:", error);
          notificationStore.showWarning(
            "しおりは作成されましたが、テンプレート画像を設定できませんでした。画像はしおり管理から追加できます。",
          );
        }
      }
    };

    // しおり作成完了時の処理
    const onShioriCreated = async (result, context = {}) => {
      console.log("しおりが作成されました:", result);
      const template = pendingShioriTemplate.value;
      const shioriNov = resolveCreatedShioriId(result, context);

      try {
        if (template && shioriNov) {
          creatingTemplateId.value = template.id;
          await createTemplateSchedules(
            template,
            shioriNov,
            context.startDate,
            context.manualImageUploaded,
            context.templateImageFile,
          );
          (context.templateWarnings || []).forEach((warning) => {
            if (warning?.message) notificationStore.showWarning(warning.message);
          });
        }

        // しおり一覧を再取得
        await shioriStore.fetchShiori({ force: true });

        // 作成されたしおりのメンバー情報を取得
        if (shioriNov) {
          await loadMembersForShioriList([{ SHIORI_NOV: shioriNov }], {
            force: true,
          });
        }

        // しおり詳細ページに遷移
        if (shioriNov) {
          router.push(`/shiori/${shioriNov}`);
        }
      } catch (error) {
        console.error("テンプレート予定作成エラー:", error);
        notificationStore.showWarning(error.rollbackError
          ? "作成に失敗し、一部データが残った可能性があります。マイページまたはゴミ箱を確認してください。"
          : "テンプレートを完全に作成できなかったため、作成内容を取り消しました。もう一度お試しください。");
      } finally {
        pendingShioriTemplate.value = null;
        creatingTemplateId.value = null;
      }
    };

    const onTemplateCreateFailed = async ({ shioriId, error }) => {
      try {
        await shioriStore.deleteShiori(shioriId);
        notificationStore.showWarning(
          "テンプレートを完全に作成できなかったため、作成内容を取り消しました。もう一度お試しください。",
        );
      } catch (rollbackError) {
        console.error("テンプレートしおりのロールバックエラー:", rollbackError);
        notificationStore.showWarning(
          "作成に失敗し、一部データが残った可能性があります。マイページまたはゴミ箱を確認してください。",
        );
      } finally {
        console.error("テンプレート作成前処理エラー:", error);
        pendingShioriTemplate.value = null;
        creatingTemplateId.value = null;
      }
    };

    const openShioriTemplateModal = (template) => {
      pendingShioriTemplate.value = template;
      showModal.value = true;
    };

    const hideBrokenTemplateImage = (event) => {
      event.currentTarget.closest(".template-card-image-wrap")?.classList.add("is-error");
    };

    const openBlankCreateModal = () => {
      pendingShioriTemplate.value = null;
      showModal.value = true;
    };

    // しおりの管理
    const handleManage = (shioriId) => {
      if (!canManageShiori(shioriId)) return;
      console.log("しおりの管理:", shioriId);
      const shiori = findShiori(shioriId);
      if (shiori) {
        selectedShiori.value = shiori;
        showManageModal.value = true;
      }
    };

    // しおりの共有
    const handleShare = (shioriId) => {
      if (!canManageShiori(shioriId)) return;
      console.log("しおりの共有:", shioriId);
      selectedShioriId.value = shioriId;
      showShareModal.value = true;
    };

    const resetConfirmDialog = () => {
      confirmDialog.value = {
        isOpen: false,
        action: null,
        shioriId: null,
        title: "",
        message: "",
        confirmText: "",
      };
    };

    const openConfirmDialog = ({ action, shioriId }) => {
      const isDelete = action === "delete";
      confirmDialog.value = {
        isOpen: true,
        action,
        shioriId,
        title: isDelete ? "しおりを削除しますか？" : "しおりをコピーしますか？",
        message: isDelete
          ? "このしおりを削除します。削除後はゴミ箱から確認できます。"
          : "このしおりのコピーを作成します。予定も一緒にコピーされます。",
        confirmText: isDelete ? "削除する" : "コピーする",
      };
    };

    const closeConfirmDialog = () => {
      if (shioriStore.isLoading) return;
      resetConfirmDialog();
    };

    const buildCopySuccessMessage = (result = {}) => {
      const copiedSchedules = Number(result.copied_schedules);
      if (Number.isFinite(copiedSchedules)) {
        return `しおりをコピーしました。${copiedSchedules}件の予定もコピーされました。`;
      }
      return "しおりをコピーしました。";
    };

    const confirmShioriAction = async () => {
      const { action, shioriId } = confirmDialog.value;
      if (!action || !shioriId) return;
      if (action === "delete" && !isOwnerShiori(shioriId)) {
        resetConfirmDialog();
        return;
      }

      try {
        if (action === "copy") {
          const result = await shioriStore.copyShiori(shioriId);
          notificationStore.showSuccess(buildCopySuccessMessage(result));
        } else if (action === "delete") {
          await shioriStore.deleteShiori(shioriId);
          notificationStore.showSuccess("しおりを削除しました。");
        }
        resetConfirmDialog();
      } catch (error) {
        console.error(
          action === "copy" ? "コピーエラー:" : "削除エラー:",
          error,
        );
        notificationStore.showError(
          action === "copy"
            ? "コピーに失敗しました。時間をおいて再度お試しください。"
            : "削除に失敗しました。時間をおいて再度お試しください。",
        );
        resetConfirmDialog();
      }
    };

    // しおりのコピー
    const handleCopy = (shioriId) => {
      console.log("しおりのコピー:", shioriId);
      openConfirmDialog({ action: "copy", shioriId });
    };

    // しおりの削除
    const handleDelete = (shioriId) => {
      if (!isOwnerShiori(shioriId)) return;
      console.log("しおりの削除:", shioriId);
      openConfirmDialog({ action: "delete", shioriId });
    };

    // しおり詳細へ遷移
    const goToShiori = (shioriId) => {
      router.push(`/shiori/${shioriId}`);
    };

    const goToCurrentSchedule = (card) => {
      router.push({
        path: `/shiori/${card.shiori.SHIORI_NOV}`,
        query: {
          tab: "schedule",
          schedule: card.item.source.LINE_NON,
        },
      });
    };

    // しおり更新完了時
    const onShioriUpdated = async () => {
      console.log("しおりが更新されました");
      await shioriStore.fetchShiori({ force: true });

      // 更新されたしおりのメンバー情報を再取得
      if (selectedShiori.value) {
        try {
          await loadMembersForShioriList([selectedShiori.value], {
            force: true,
          });
        } catch (error) {
          console.error("メンバー再取得エラー:", error);
        }
      }
    };

    // しおり削除完了時
    const onShioriDeleted = () => {
      console.log("しおりが削除されました");
    };

    const handleFloatingAddClick = () => {
      if (mainTab.value === "shiori") {
        showModal.value = true;
      } else {
        showCreateBoardModal.value = true;
      }
    };

    const handleCreateBoard = async () => {
      boardError.value = "";
      if (!newBoardTitle.value || !newBoardTitle.value.trim()) {
        boardError.value = "ボード名は必須です";
        return;
      }
      if (newBoardTitle.value.trim().length > 255) {
        boardError.value = "ボード名は255文字以内で入力してください";
        return;
      }

      try {
        const result = await boardStore.createBoard(newBoardTitle.value.trim());
        newBoardTitle.value = "";
        showCreateBoardModal.value = false;
        const boardId = result?.BOARD_ID || result?.board_id;
        if (boardId) {
          router.push(`/destination-board/${boardId}`);
        } else {
          await boardStore.fetchBoards();
        }
      } catch (err) {
        boardError.value =
          err.response?.data?.detail || "ボードの作成に失敗しました";
      }
    };

    const closeCreateBoardModal = () => {
      showCreateBoardModal.value = false;
      newBoardTitle.value = "";
      boardError.value = "";
    };

    const goToBoard = (boardId) => {
      router.push(`/destination-board/${boardId}`);
    };

    const createIdempotencyKey = () => {
      if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
      return `template-copy-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const availableTemplateKeys = computed(
      () =>
        new Set(
          boardStore.templates
            .filter((template) => template.candidateCount === 5)
            .map((template) => template.templateKey),
        ),
    );

    const copyDestinationBoardTemplate = async (templateKey) => {
      if (copyingTemplateKey.value) return;
      copyingTemplateKey.value = templateKey;
      templateCopyError.value = "";
      failedTemplateKey.value = null;
      const idempotencyKey =
        templateCopyAttemptKeys.get(templateKey) || createIdempotencyKey();
      templateCopyAttemptKeys.set(templateKey, idempotencyKey);
      try {
        const result = await boardStore.copyBoardTemplate(
          templateKey,
          idempotencyKey,
        );
        if (!result?.boardId) throw new Error("Copied board ID is missing");
        templateCopyAttemptKeys.delete(templateKey);
        await router.push(`/destination-board/${result.boardId}`);
      } catch (err) {
        failedTemplateKey.value = templateKey;
        templateCopyError.value =
          err.response?.data?.detail ||
          "テンプレートのコピーに失敗しました。";
      } finally {
        copyingTemplateKey.value = null;
      }
    };

    const templateButtonLabel = (template) => {
      if (copyingTemplateKey.value === template.templateKey) {
        return `${template.title}をコピー中`;
      }
      if (failedTemplateKey.value === template.templateKey) {
        return `${template.title}のコピーを再試行`;
      }
      return `${template.title}をコピーして使う`;
    };

    const goToMainTab = (tab) => {
      if (tab === "shiori") {
        router.push("/mypage");
      } else {
        router.push("/destination-boards");
      }
    };

    return {
      mainTab,
      showModal,
      showManageModal,
      showShareModal,
      showCreateBoardModal,
      newBoardTitle,
      boardError,
      copyingTemplateKey,
      templateCopyError,
      templateCatalogError,
      isTemplateCatalogLoading,
      failedTemplateKey,
      availableTemplateKeys,
      selectedShiori,
      selectedShioriId,
      shioriStore,
      boardStore,
      shioriMembers,
      activeTab,
      searchQuery,
      currentPage,
      creatingTemplateId,
      confirmDialog,
      createModalPrefillData,
      shioriTemplates,
      destinationBoardTemplates,
      hideBrokenTemplateImage,
      totalPages,
      currentLiveCards,
      pagedShiori,
      filteredBySearch,
      domesticShiori,
      internationalShiori,
      visitedDomesticShiori,
      visitedInternationalShiori,
      filteredShiori,
      domesticDestinationCount,
      internationalDestinationCount,
      TOTAL_PREFECTURES,
      TOTAL_COUNTRIES,
      formatDate,
      getTotalCost,
      getShioriPermission,
      selectedCanWrite,
      selectedCanManage,
      selectedIsOwner,
      onShioriCreated,
      onTemplateCreateFailed,
      openShioriTemplateModal,
      openBlankCreateModal,
      handleManage,
      handleShare,
      handleCopy,
      handleDelete,
      closeConfirmDialog,
      confirmShioriAction,
      goToShiori,
      goToCurrentSchedule,
      onShioriUpdated,
      onShioriDeleted,
      handleFloatingAddClick,
      handleCreateBoard,
      closeCreateBoardModal,
      goToBoard,
      copyDestinationBoardTemplate,
      loadDestinationBoardTemplates,
      templateButtonLabel,
      goToMainTab,
    };
  },
};
</script>

<style scoped>
.mypage {
  min-height: 100vh;
  background-color: var(--color-background);
}

.mypage-main-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  background-color: var(--color-surface);
  border-radius: 12px;
  padding: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.main-tab-btn {
  flex: 1;
  text-align: center;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  border-radius: 9px;
  transition: all 0.2s ease;
}

.main-tab-btn:hover {
  color: var(--color-text);
  background-color: #f8fafc;
}

.main-tab-btn.active {
  background-color: #b4cf9e;
  color: white;
}

.mypage-content {
  padding: 1.5rem 0.75rem; /* mobile */
}

.mypage-unified-container {
  max-width: 1040px !important;
  margin: 0 auto;
  padding: 0 0 40px;
}

/* ── ヘッダー（評価ダッシュボードと統一） ── */
.dashboard-header {
  margin-bottom: 1.5rem;
}

.dashboard-kicker {
  color: var(--color-primary-ink);
  font-size: 0.8125rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.25rem;
}

.dashboard-title {
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: var(--color-text);
  margin: 0 0 0.5rem;
}

.dashboard-desc {
  color: var(--color-text-muted);
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0;
}

/* ── 検索バー + ツールバー ── */
.search-and-tools-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.search-and-tools-bar .search-bar {
  flex: 1;
  margin-bottom: 0;
}

.trash-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.85rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  transition: all var(--transition-fast);
  white-space: nowrap;
  flex-shrink: 0;
}

.trash-link-btn:hover {
  border-color: #fca5a5;
  color: #ef4444;
  background: #fef2f2;
}


.live-now-list {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.live-now-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: center;
  width: 100%;
  padding: 0.95rem 1rem;
  background: var(--color-surface);
  border: 1px solid #86efac;
  border-left: 5px solid #16a34a;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
  transition: box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

.live-now-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.live-now-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0.25rem 0.55rem;
  background: #dcfce7;
  border-radius: var(--radius-full);
  color: #166534;
  font-size: 0.75rem;
  font-weight: 800;
  white-space: nowrap;
}

.live-now-main {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.live-now-trip {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.live-now-main strong {
  min-width: 0;
  font-size: 1rem;
  overflow-wrap: anywhere;
}

.live-now-main span:last-child {
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  overflow-wrap: anywhere;
}

.live-now-arrow {
  color: var(--primary-hover);
  font-size: 1.25rem;
  font-weight: 800;
}

/* タブスタイル */
.destination-tabs {
  display: flex;
  gap: 0.375rem; /* mobile */
  justify-content: flex-start; /* mobile */
  margin-bottom: 1.25rem; /* mobile */
  flex-wrap: nowrap; /* mobile: scroll instead of wrap */
  overflow-x: auto; /* mobile */
  padding-bottom: 0.25rem; /* mobile */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.destination-tabs::-webkit-scrollbar {
  display: none;
}

.tab-button {
  padding: 0.45rem 1rem; /* mobile */
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  font-size: 0.8125rem; /* mobile */
  font-weight: 700;
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: none;
  white-space: nowrap; /* mobile */
  flex-shrink: 0; /* mobile */
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.tab-button:hover {
  border-color: var(--color-primary-ink);
  background: var(--color-background);
}

.tab-button.active {
  border-color: #b4cf9e;
  background: #b4cf9e;
  color: #ffffff;
  box-shadow: var(--shadow-sm);
}

.tab-button.active:hover {
  background: #a3c489;
  border-color: #a3c489;
}

/* 地図とリストのコンテナ */
.map-and-list-container {
  display: flex;
  flex-direction: column; /* mobile */
  gap: 1.25rem; /* mobile */
  margin-bottom: 2rem;
  height: auto; /* mobile */
  min-height: auto; /* mobile */
}

.map-section {
  flex: none; /* mobile */
  min-width: 0;
  background: var(--color-surface);
  border-radius: 16px;
  padding: 0.75rem; /* mobile */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: min(68vh, 560px); /* mobile */
  min-height: 440px;
}

/* 渡航先カウント表示カード */
.destination-count-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  color: white;
  flex: 0 0 auto;
}

.count-icon {
  font-size: 2rem; /* mobile */
  opacity: 0.9;
}

.count-info {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.count-label {
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.9;
}

.count-number {
  font-size: 1.5rem; /* mobile */
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 0.15rem;
}

.count-current {
  font-size: 1.5rem; /* mobile */
}

.count-separator {
  font-size: 1.125rem; /* mobile */
  opacity: 0.8;
  margin: 0 0.1rem;
}

.count-total {
  font-size: 1.125rem; /* mobile */
  opacity: 0.85;
}

.count-unit {
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.9;
}

.list-section {
  flex: none; /* mobile */
  min-width: 0;
  overflow-y: auto;
  max-height: none; /* mobile */
}

/* ========================================
   レスポンシブ - モバイルファースト
   ======================================== */

/* タブレット (768px〜) */
@media (min-width: 768px) {
  .mypage-content {
    padding: 2rem 1rem;
  }

  .page-header {
    margin-bottom: 2.5rem;
  }

  .page-title {
    font-size: 1.875rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }


  .live-now-list {
    margin-bottom: 1.25rem;
  }

  .destination-tabs {
    gap: 0.625rem;
    margin-bottom: 1.5rem;
    overflow-x: visible;
    flex-wrap: wrap;
    padding-bottom: 0;
    scrollbar-width: auto;
    -ms-overflow-style: auto;
    justify-content: center;
  }

  .destination-tabs::-webkit-scrollbar {
    display: block;
  }

  .tab-button {
    padding: 0.625rem 1.25rem;
    font-size: 0.9375rem;
    white-space: normal;
    flex-shrink: 1;
  }

  .map-and-list-container {
    height: auto;
    gap: 1.5rem;
  }

  .map-section {
    height: min(72vh, 660px);
    min-height: 540px;
    padding: 1rem;
  }

  .shiori-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .list-section {
    max-height: none;
  }

  .count-icon {
    font-size: 2.25rem;
  }

  .count-number,
  .count-current {
    font-size: 1.75rem;
  }

  .count-separator,
  .count-total {
    font-size: 1.25rem;
  }
}

/* デスクトップ (1024px〜) */
@media (min-width: 1024px) {
  .mypage-content {
    padding: 3rem 1rem;
  }

  .page-header {
    margin-bottom: 3rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .page-subtitle {
    font-size: 1.125rem;
  }

  .trash-link {
    padding: 0.5rem 1rem;
  }

  .trash-text {
    display: inline; /* tablet+: show text */
  }

  .destination-tabs {
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .tab-button {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }

  .map-and-list-container {
    flex-direction: row;
    align-items: stretch;
    height: calc(100vh - 220px);
    min-height: 700px;
    gap: 1.5rem;
  }

  .map-section {
    flex: 0 0 min(74%, 980px);
    height: auto;
    padding: 1rem;
    gap: 1rem;
  }

  .shiori-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  .list-section {
    flex: 1 1 280px;
    max-height: 100%;
  }

  .count-icon {
    font-size: 2.5rem;
  }

  .count-number,
  .count-current {
    font-size: 2rem;
  }

  .count-separator,
  .count-total {
    font-size: 1.5rem;
  }
}

/* 大画面 (1280px〜) */
@media (min-width: 1280px) {
  .map-and-list-container {
    height: calc(100vh - 200px);
    min-height: 760px;
  }

  .map-section {
    flex-basis: min(78%, 1180px);
  }

  .shiori-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
}

/* ローディング状態 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: var(--color-text-muted);
}

/* 空状態 */
.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
}

.empty-description {
  font-size: 1rem;
  color: var(--color-text-muted);
  margin-bottom: 0;
}

/* しおりグリッド */
.shiori-grid {
  display: grid;
  /* grid-template-columns と gap はレスポンシブ定義で上書きされるためベースでは省略 */
}

/* 検索バー */
.search-bar {
  position: relative;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  padding: 0.625rem 2.5rem 0.625rem 1rem;
  font-size: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  transition: all var(--transition-fast);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}

.search-clear-btn {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--color-text-light);
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
}

.search-clear-btn:hover {
  color: var(--color-text);
}

/* ページネーション */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 0.5rem 0;
}

.page-btn {
  padding: 0.5rem 1.25rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.page-btn:hover:not(:disabled) {
  background: var(--color-primary-light);
  border-color: var(--primary-color);
  color: var(--primary-hover);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-muted);
  min-width: 5rem;
  text-align: center;
}

.shiori-template-section {
  margin-top: 2rem;
  padding: 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.template-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.template-kicker {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 800;
}

.template-section-heading h2 {
  margin: 0;
  color: var(--color-text);
  font-size: 1.125rem;
  line-height: 1.35;
}

.template-count {
  flex-shrink: 0;
  padding: 0.25rem 0.6rem;
  color: var(--primary-hover);
  background: var(--color-primary-light);
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 800;
}

.shiori-template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.75rem;
}

.shiori-template-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  min-width: 0;
  padding: 0.875rem;
  color: var(--color-text);
  text-align: left;
  background: var(--color-background);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  font: inherit;
  transition: border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.template-card-image-wrap {
  grid-column: 1 / -1;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-md);
  background: var(--color-primary-light);
}

.template-card-image-wrap.is-error {
  display: none;
}

.template-card-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shiori-template-card:hover:not(:disabled) {
  border-color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.shiori-template-card:focus-visible {
  outline: 3px solid var(--primary-hover, #4f7d3e);
  outline-offset: 4px;
  border-color: var(--primary-hover, #4f7d3e);
  box-shadow: 0 0 0 2px var(--bg-primary, #fff), var(--shadow-md);
}

.shiori-template-card:disabled {
  cursor: wait;
  opacity: 0.7;
}

.template-card-main {
  display: grid;
  min-width: 0;
  gap: 0.2rem;
}

.template-card-main strong {
  overflow: hidden;
  color: var(--color-text);
  font-size: 0.9rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-card-main small {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  line-height: 1.35;
}

.template-card-meta {
  flex-shrink: 0;
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 800;
}

.native-confirm-overlay {
  z-index: 2500;
}

.native-confirm-modal {
  width: min(100%, 360px);
  max-height: calc(100dvh - 2rem);
  padding: 1rem;
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: var(--shadow-xl);
  overflow-y: auto;
}

.native-confirm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.native-confirm-title {
  margin: 0;
  color: var(--color-text);
  font-size: 1.125rem;
  line-height: 1.4;
}

.native-confirm-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: var(--color-background);
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
}

.native-confirm-message {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.9375rem;
  line-height: 1.65;
}

.native-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

.native-confirm-cancel,
.native-confirm-accept {
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
}

.native-confirm-cancel {
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text);
}

.native-confirm-accept {
  border: 1px solid var(--primary-color);
  background: var(--primary-color);
  color: white;
}

.native-confirm-accept.danger {
  border-color: var(--color-danger);
  background: #dc2626;
}

.native-confirm-cancel:disabled,
.native-confirm-accept:disabled,
.native-confirm-close:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* モバイル (〜640px) グリッドは1列 */
@media (max-width: 640px) {
  .mypage-content {
    padding: 1rem 0;
  }

  .page-header {
    margin-bottom: 1.25rem;
  }

  .page-title {
    font-size: 1.25rem;
  }

  .destination-tabs {
    margin-bottom: 0.875rem;
  }

  .shiori-template-section {
    margin: 1.5rem 0.75rem 0;
  }

  .shiori-template-grid {
    grid-template-columns: 1fr;
  }

  .shiori-template-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .template-card-meta {
    grid-column: 2;
  }

  .tab-button {
    padding: 0.375rem 0.75rem;
    border-width: 1px;
    font-size: 0.75rem;
  }

  .live-now-card {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.5rem;
    padding: 0.8rem;
  }

  .live-now-badge {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .shiori-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .map-section {
    height: min(84vh, 620px);
    min-height: 520px;
    padding: 0.5rem;
    border-radius: var(--radius-md);
    gap: 0.5rem;
  }

  .destination-count-card {
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
  }

  .count-icon,
  .count-number,
  .count-current {
    font-size: 1.25rem;
  }

  .count-label,
  .count-unit {
    font-size: 0.75rem;
  }

  .count-separator,
  .count-total {
    font-size: 1rem;
  }
}
/* メインタブ */
.main-tabs-container {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  padding-bottom: 0.75rem;
}

.main-tab-btn {
  padding: 0.625rem 1.75rem;
  border: none;
  background: transparent;
  font-size: 1.05rem;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.25s ease;
}

.main-tab-btn:hover {
  color: #1e293b;
  background: rgba(241, 245, 249, 0.8);
}

.main-tab-btn.active {
  color: #ffffff;
  background: linear-gradient(135deg, #5e7e4c 0%, #455d38 100%);
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.25);
}

.ai-trip-header-link {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-flex;
  align-items: center;
  min-height: 2.25rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: #2563eb;
  color: white;
  font-size: 0.8125rem;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 6px 14px rgba(37, 99, 235, 0.2);
}

.ai-trip-header-link:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

@media (max-width: 640px) {
  .ai-trip-header-link {
    position: static;
    margin: 0.75rem auto 0;
  }
}

/* AI提案カード */
.ai-suggest-card {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.25rem;
  padding: 1rem;
  color: white;
  text-decoration: none;
  background: linear-gradient(135deg, #b4cf9e 0%, #c8dbc0 65%, #9cbcd4 100%);
  border-radius: 16px;
  box-shadow: 0 8px 20px rgba(180, 207, 158, 0.22);
  transition: transform 0.2s, box-shadow 0.2s;
}

.ai-suggest-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(180, 207, 158, 0.28);
}

.destination-template-section {
  margin-top: 2.5rem;
  margin-bottom: 2rem;
  padding: 1rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  background: linear-gradient(145deg, #f7faf3 0%, #ffffff 58%, #eef5e9 100%);
  box-shadow: var(--shadow-sm);
}

.destination-template-lead {
  margin: -0.35rem 0 1rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.6;
}

.destination-template-error {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid #c35c4d;
  border-radius: var(--radius-sm);
  background: #fff2ef;
  color: #8a2d22;
  font-weight: 700;
}

.destination-template-reload {
  margin-left: 0.75rem;
  padding: 0.4rem 0.7rem;
  border: 1px solid currentColor;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.destination-template-status {
  color: var(--color-text-muted);
  font-weight: 700;
}

.destination-template-unavailable {
  color: #8a2d22 !important;
  font-weight: 700;
}

.destination-template-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.destination-template-kicker {
  display: block;
  margin-bottom: 0.25rem;
  color: #416337;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.destination-template-heading h2 {
  margin: 0;
  color: var(--color-text);
  font-size: 1.125rem;
}

.destination-template-count {
  flex-shrink: 0;
  padding: 0.25rem 0.6rem;
  border-radius: var(--radius-full);
  background: #dcebd1;
  color: #35582c;
  font-size: 0.75rem;
  font-weight: 800;
}

.destination-template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.75rem;
}

.destination-template-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  padding: 1rem;
  border: 1px solid #d9e5d2;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.88);
  color: var(--color-text);
  text-decoration: none;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast);
}

.destination-template-card:disabled {
  cursor: wait;
  opacity: 0.62;
}

.destination-template-card:hover:not(:disabled) {
  border-color: #719461;
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.destination-template-card:focus-visible {
  outline: 3px solid #4f7d3e;
  outline-offset: 3px;
}

.destination-template-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 12px;
  background: #e5f0dc;
  color: #416337;
}

.destination-template-copy {
  display: grid;
  min-width: 0;
  gap: 0.2rem;
}

.destination-template-copy small {
  color: #527348;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.destination-template-copy strong {
  font-size: 0.95rem;
  line-height: 1.4;
}

.destination-template-copy > span {
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.5;
}

.destination-template-arrow {
  color: #416337;
  font-size: 1.25rem;
  font-weight: 800;
}

@media (prefers-reduced-motion: reduce) {
  .destination-template-card {
    transition: none;
  }

  .destination-template-card:hover:not(:disabled) {
    transform: none;
  }
}

.ai-suggest-icon {
  display: grid;
  width: 48px;
  height: 48px;
  flex: 0 0 auto;
  place-items: center;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 14px;
}

.ai-suggest-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.ai-suggest-copy strong {
  font-size: 1rem;
}

.ai-suggest-copy > span:last-child {
  color: rgba(255, 255, 255, 0.86);
  font-size: 0.78rem;
}

.ai-suggest-label {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.ai-suggest-arrow {
  font-size: 1.5rem;
  font-weight: 800;
}

/* 検討ボードカード */
.board-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.02),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.board-card:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.06);
  border-color: rgba(180, 207, 158, 0.3);
}

.board-card-content {
  flex: 1;
}

.board-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.board-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: #64748b;
}

.board-members {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.board-arrow {
  font-size: 1.25rem;
  font-weight: 700;
  color: #94a3b8;
  margin-left: 1rem;
}

.board-create-overlay {
  position: fixed;
  inset: 0;
  z-index: 150;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.66);
  backdrop-filter: blur(4px);
}

.board-create-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 28rem;
  max-height: calc(100vh - 8rem);
  overflow: hidden auto;
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
}

.board-create-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
}

.board-create-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 700;
}

.board-create-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 1.75rem;
}

.board-create-close:hover:not(:disabled) {
  background: #f1f5f9;
  color: #1e293b;
}

.board-create-form {
  width: 100%;
  padding: 1.5rem;
}

.board-create-form .form-input:focus {
  outline: 0;
  border-color: #b4cf9e;
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.2);
}

.board-create-error {
  margin: 1.5rem 1.5rem 0;
  padding: 0.875rem 1rem;
  border-left: 3px solid #b91c1c;
  border-radius: var(--radius-md);
  background: #fef2f2;
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 600;
}

.board-create-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border-light);
}

.board-create-footer .btn {
  min-height: 2.75rem;
  padding: 0.7rem 1.25rem;
  border-radius: 10px;
  font-weight: 700;
}

.board-create-secondary {
  border: 1px solid #94a3b8;
  background: var(--color-surface);
  color: #334155;
}

.board-create-secondary:hover:not(:disabled) {
  border-color: #64748b;
  background: #f8fafc;
}

.board-create-primary {
  border: 0;
  background: linear-gradient(135deg, #35536b 0%, #4f708a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(53, 83, 107, 0.3);
}

.board-create-primary:hover:not(:disabled) {
  background: #35536b;
  box-shadow: 0 6px 16px rgba(53, 83, 107, 0.4);
  transform: translateY(-1px);
}

.board-create-close:focus-visible,
.board-create-secondary:focus-visible,
.board-create-primary:focus-visible {
  outline: 3px solid #35536b;
  outline-offset: 2px;
}

.board-create-close:disabled,
.board-create-secondary:disabled,
.board-create-primary:disabled,
.board-create-form .form-input:disabled {
  cursor: not-allowed;
}

.board-create-primary:disabled {
  background: #4f708a;
  box-shadow: none;
  opacity: 1;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .board-create-modal {
  animation: board-modal-slide-up 0.3s ease-out;
}

.modal-fade-leave-active .board-create-modal {
  animation: board-modal-slide-down 0.3s ease-out;
}

@keyframes board-modal-slide-up {
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes board-modal-slide-down {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(20px) scale(0.95); }
}

@media (min-width: 768px) {
  .ai-suggest-card {
    padding: 1.15rem 1.35rem;
  }

  .ai-suggest-copy strong {
    font-size: 1.1rem;
  }

  .mypage-main-tabs {
    display: none !important;
  }
}

@media (max-width: 767px) {
  .mypage-main-tabs {
    display: none !important;
  }

  .mypage-unified-container {
    padding: 1rem 0.75rem calc(5rem + env(safe-area-inset-bottom, 0)) !important;
  }

  .shiori-grid {
    grid-template-columns: 1fr !important;
    gap: 0.65rem !important;
  }

  .destination-tabs {
    display: flex !important;
    overflow-x: auto !important;
    scrollbar-width: none;
    padding-bottom: 0.25rem;
    gap: 0.4rem;
  }
  .destination-tabs::-webkit-scrollbar {
    display: none;
  }

  .destination-tabs .tab-button {
    flex-shrink: 0;
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    white-space: nowrap;
    border-radius: var(--radius-full);
  }

  .search-and-tools-bar {
    margin-bottom: 0.75rem;
  }

  .ai-suggest-card {
    border-radius: var(--radius-lg);
    padding: 1rem;
  }

  .board-card {
    border-radius: var(--radius-lg);
    padding: 0.875rem 1rem;
  }
}
</style>
